import { generators } from '@playcastdotio/Messaging';
import { ipcSend } from '../../../api/messaging/protocols/ipc';
import { driverState } from '../../../api/stores/shared';
import {
  parseMacroStep,
  type ParsedMacroStep,
  type ParsedMouseStep,
  type ParsedTouchStep,
} from './MacroStepParser';
import { MacroKeyboardEmitter } from './input/MacroKeyboardEmitter';
import { MacroGamepadEmitter } from './input/MacroGamepadEmitter';
import {
  RealMacroMouseEmitter,
  isRecordedClickPair,
  isValidMacroGuestFrame,
  MOUSE_MODE_ABSOLUTE,
  MOUSE_MODE_DEFAULT,
  UnplaceableClickError,
  type MacroGuestFrame,
} from './input/MacroMouseEmitter';
import { MacroTouchEmitter } from './input/MacroTouchEmitter';

type ParsedLegacyStep = ParsedMacroStep;

const MIN_CLICK_HOLD_MS = 80;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export type MacroStepRunResult = { success: true } | { success: false; reason: string };

/**
 * Raised when a macro needs an InputCaster (mouse/keyboard) the host reports
 * as unavailable. Mirrors the fail-closed handling of UnplaceableClickError:
 * the walk aborts loudly instead of silently delivering zero input.
 */
export class MacroInputUnavailableError extends Error {
  public readonly device: 'mouse' | 'keyboard';
  public constructor(device: 'mouse' | 'keyboard') {
    super(`Macro requires ${device} input but the ${device} InputCaster is unavailable`);
    this.name = 'MacroInputUnavailableError';
    this.device = device;
  }
}

/**
 * Gate the walk on InputCaster availability. Reads the shared `driverState`
 * store; when a required device is explicitly reported unavailable, returns
 * the device name so the runner can fail loudly.
 *
 * The gate is injectable for tests; by default it consults the live store.
 * When the store has no signal yet (empty / fields undefined — e.g. before
 * the first driver-status poll), the gate passes, so a warm production host
 * that never reported the field doesn't get a false failure.
 */
export type MacroInputGate = (steps: string[]) => 'mouse' | 'keyboard' | null;

export function createDriverStateInputGate(): MacroInputGate {
  return (steps: string[]): 'mouse' | 'keyboard' | null => {
    const state = driverState.currentState as Partial<{ inputCasterMouse?: boolean; inputCasterKeyboard?: boolean }>;
    const needsMouse = steps.some((step) => step.startsWith('mouse='));
    const needsKeyboard = steps.some((step) => step.startsWith('key=') || step.startsWith('string='));
    if (needsMouse && state.inputCasterMouse === false) return 'mouse';
    if (needsKeyboard && state.inputCasterKeyboard === false) return 'keyboard';
    return null;
  };
}

export class MacroStepRunner {
  private readonly keyboard = new MacroKeyboardEmitter();
  private readonly gamepad = new MacroGamepadEmitter();
  private readonly touch = new MacroTouchEmitter();

  public constructor(private readonly inputGate: MacroInputGate = createDriverStateInputGate()) {}

  public async runSteps(
    steps: string[],
    label = 'Macro',
    frame?: MacroGuestFrame,
  ): Promise<MacroStepRunResult> {
    if (!steps || steps.length === 0) {
      console.log(`[MacroManager] No steps for ${label}, skipping.`);
      return { success: true };
    }

    const blockedDevice = this.inputGate(steps);
    if (blockedDevice) {
      // A recorded macro that needs an unavailable InputCaster must NOT finish
      // as a silent success — that is exactly the "walk succeeded but zero
      // input was delivered" failure mode (G8). Return a typed failure rather
      // than throwing, so callers observe a clean failure result.
      return {
        success: false as const,
        reason: new MacroInputUnavailableError(blockedDevice).message,
      };
    }

    let absoluteModeSent = false;
    let mouseDownAtMs: number | null = null;
    const onFirstAbsoluteMode = () => {
      if (absoluteModeSent) return;
      console.log('[MacroManager] setMode absolute before mouse=abs');
      ipcSend(generators.mouse.setMode('host', MOUSE_MODE_ABSOLUTE));
      absoluteModeSent = true;
    };
    const mouseEmitter = new RealMacroMouseEmitter(onFirstAbsoluteMode, label);

    try {
      ipcSend(
        generators.input.toggleFilters('host', {
          keyboard: false,
          mouse: false,
          gamepad: false,
          touch: false,
        }),
      );
      await delay(100);

      console.log(`[MacroManager] Running ${label} with ${steps.length} steps.`);
      if (isValidMacroGuestFrame(frame)) {
        console.log(`[MacroManager] ${label} guest frame ${frame.width}x${frame.height}`);
      }

      for (let i = 0; i < steps.length; i += 1) {
        const rawStep = steps[i];
        const step = parseMacroStep(rawStep);
        if (!step) continue;

        if (step.type === 'key') {
          const legacyStep = step as ParsedLegacyStep;
          const keyCode = Number(legacyStep.value);
          if (Number.isNaN(keyCode)) {
            console.warn('[MacroManager] Invalid key code in step:', rawStep);
            continue;
          }
          console.log(`[MacroManager] Sending key press: ${keyCode}`);
          await delay(legacyStep.timing);
          await this.keyboard.sendKeyPress(keyCode, false, legacyStep.hold);
        } else if (step.type === 'string') {
          const legacyStep = step as ParsedLegacyStep;
          console.log(`[MacroManager] Sending string: ${legacyStep.value}`);
          await delay(legacyStep.timing);
          await this.keyboard.sendKeyString(legacyStep.value);
        } else if (step.type === 'gpad') {
          const legacyStep = step as ParsedLegacyStep;
          console.log(`[MacroManager] Sending gpad: ${legacyStep.value}`);
          await delay(legacyStep.timing);
          await this.gamepad.sendGamepadTap(legacyStep.value, legacyStep.hold);
        } else if (step.type === 'mouse') {
          const mouseStep = step as ParsedMouseStep;
          const nextParsed = i + 1 < steps.length ? parseMacroStep(steps[i + 1]) : null;
          const nextMouse = nextParsed?.type === 'mouse' ? nextParsed as ParsedMouseStep : null;

          if (isRecordedClickPair(mouseStep, nextMouse)) {
            await delay(mouseStep.timing);
            const placed = await mouseEmitter.emitClick(
              { abs: mouseStep, down: nextMouse },
              frame ?? { width: 0, height: 0 },
            );
            if (!placed) {
              console.warn('[MacroManager] Recorded click could not be placed — aborting macro', {
                label,
                frame: frame ?? null,
                normalized: { x: mouseStep.params.x, y: mouseStep.params.y },
              });
              throw new UnplaceableClickError(label);
            }
            mouseDownAtMs = Date.now();
            i += 1;
            continue;
          }

          await delay(mouseStep.timing);
          if (mouseStep.action === 'up' && mouseDownAtMs !== null) {
            const remainingHoldMs = MIN_CLICK_HOLD_MS - (Date.now() - mouseDownAtMs);
            if (remainingHoldMs > 0) await delay(remainingHoldMs);
          }
          await mouseEmitter.sendMouseAction(mouseStep, frame);
          if (mouseStep.action === 'down') mouseDownAtMs = Date.now();
        } else if (step.type === 'touch') {
          const touchStep = step as ParsedTouchStep;
          await delay(touchStep.timing);
          await this.touch.sendTouchAction(touchStep);
        }
      }

      console.log(`[MacroManager] ${label} macro complete.`);
      return { success: true };
    } catch (error) {
      if (error instanceof UnplaceableClickError || error instanceof MacroInputUnavailableError) {
        return { success: false, reason: error.message };
      }
      console.error('[MacroManager] Failed to run macro steps:', error);
      throw error;
    } finally {
      if (absoluteModeSent) {
        console.log('[MacroManager] Restoring mouse setMode default after abs playback');
        ipcSend(generators.mouse.setMode('host', MOUSE_MODE_DEFAULT));
      }
      ipcSend(
        generators.input.toggleFilters('host', {
          keyboard: true,
          mouse: true,
          gamepad: true,
          touch: true,
          exitGuestModeAfterAdminSession: false,
        }),
      );
    }
  }
}

export type { MacroGuestFrame };
